/**
 * AxisRangeControl - Renders axis range selection control
 *
 * Displays a SelectableAxis component for choosing a range of values
 * along an axis (e.g., step range in grid mode). Used for filtering/zooming.
 */

import {expandRangeToNElements} from '@wandb/weave/common/util/data';
import React from 'react';

import {SelectableAxis} from '../../Axis';
import {MediaBrowserPanelProps} from '../Component';
import {GridData} from '../layout/types';

export type AxisRangeControlProps = {
  axis: 'xAxis' | 'yAxis';
  axisLabel: string;
  gridData: GridData;
  config: any;
  width: number;
  columnCount?: number;
  updateConfig: MediaBrowserPanelProps['updateConfig'];
  direction?: 'top' | 'bottom' | 'left' | 'right';
};

/**
 * Renders an axis range selection control.
 *
 * @param props - AxisRangeControlProps
 * @returns React element
 */
export const AxisRangeControl: React.FC<AxisRangeControlProps> = ({
  axis,
  axisLabel,
  gridData,
  config,
  width,
  columnCount,
  updateConfig,
  direction = 'bottom',
}) => {
  const ticks =
    axis === 'xAxis'
      ? gridData.metadata.xAxisTicks
      : gridData.metadata.yAxisTicks;

  const allAxisValues =
    axis === 'xAxis'
      ? gridData.metadata.allXAxisValues
      : gridData.metadata.allYAxisValues;

  const selection =
    axis === 'xAxis' ? config.selection?.xAxis : config.selection?.yAxis;

  const handleSelect = React.useCallback(
    (range: [number, number]) => {
      // Use ref to get current config to avoid stale closures
      const currentConfig = config;

      if (axis === 'xAxis' && columnCount && allAxisValues) {
        // For x-axis with column count, expand range to fill columns
        const newRange = expandRangeToNElements(
          range,
          allAxisValues,
          columnCount
        );

        // Always update config to persist changes
        // SelectableAxis will handle its own internal state
        const newSelection = {...currentConfig.selection, xAxis: newRange};

        updateConfig({
          selection: newSelection,
        });

        return newRange;
      }

      // For all other cases, we still need to update config to persist the selection
      // (SelectableAxis syncs to props.selection on each render via useEffect)
      // Always update to persist
      const newSelection = {...currentConfig.selection, [axis]: range};

      updateConfig({
        selection: newSelection,
      });

      return range;
    },
    [axis, columnCount, config, allAxisValues, updateConfig]
  );

  const handleClear = React.useCallback(() => {
    const currentConfig = config;

    const newSelection = {
      ...currentConfig.selection,
      [axis]: undefined,
    };

    updateConfig({
      selection: newSelection,
    });
  }, [axis, config, updateConfig]);

  // Don't render if we don't have the required data (match original behavior)
  if (!ticks || !allAxisValues) {
    return null;
  }

  // Validate selection - ensure it doesn't have undefined values
  const validSelection: [number, number] | undefined =
    selection &&
    Array.isArray(selection) &&
    selection.length === 2 &&
    typeof selection[0] === 'number' &&
    typeof selection[1] === 'number'
      ? (selection as [number, number])
      : undefined;

  return (
    <SelectableAxis
      axisLabel={axisLabel}
      ticks={allAxisValues}
      direction={direction}
      width={width}
      selection={validSelection}
      onSelect={handleSelect}
      onClear={handleClear}
    />
  );
};
